import { Injectable } from '@angular/core';
import {
  AudioExperienceId,
  ConsumptionTimeId,
  FictionInterestId,
  KindleFrictionId,
  ListeningTimeId,
  MotivationId,
  NonFictionInterestId,
  TimeForReadingId,
  VisionCareId,
} from 'funnel/src/app/data/quiz';
import {
  formatList,
  getAnswerIds,
  getFirstAnswerId,
  getMomentumScore,
  hasEyeComfortConcern,
  isAllowedValue,
  listeningPlans,
  QuizAnswers,
  ResultMessages,
} from 'funnel/src/app/data/result-helpers';

declare const $localize: any;

export interface KindleResultMessageParams {
  answers: QuizAnswers;
  motivationIds: MotivationId[];
  fictionInterestIds: FictionInterestId[];
  nonFictionInterestIds: NonFictionInterestId[];
}

@Injectable({
  providedIn: 'root',
})
export class KindleResultMessageService {
  private readonly frictionPriority: KindleFrictionId[] = [
    'noQuietTime',
    'wantHandsFree',
    'longChapters',
    'fallAsleep',
    'voicesMatter',
  ];

  private readonly frictionTextMap: Record<KindleFrictionId, string> = {
    noQuietTime: $localize`:@@kindleResultFrictionQuiet:limited quiet time`,
    fallAsleep: $localize`:@@kindleResultFrictionSleep:sleepy evening reading`,
    longChapters: $localize`:@@kindleResultFrictionChapters:hard-to-start chapters`,
    wantHandsFree: $localize`:@@kindleResultFrictionHandsFree:hands-free moments`,
    voicesMatter: $localize`:@@kindleResultFrictionVoices:natural voice quality`,
  };

  private readonly windowTextMap: Record<ConsumptionTimeId, string> = {
    inMorning: $localize`:@@kindleResultWindowMorning:your morning reset`,
    workingOut: $localize`:@@kindleResultWindowWorkout:workouts or walks`,
    onCommute: $localize`:@@kindleResultWindowCommute:commutes and errands`,
    takingBreak: $localize`:@@kindleResultWindowBreak:short breaks`,
  };

  private readonly fictionTextMap: Record<FictionInterestId, string> = {
    contemporaryRomance: $localize`:@@kindleResultFictionRomance:relationship-driven fiction`,
    mysteryThriller: $localize`:@@kindleResultFictionThriller:mystery and suspense`,
    fantasy: $localize`:@@kindleResultFictionFantasy:immersive fantasy`,
    scienceFiction: $localize`:@@kindleResultFictionSciFi:big-idea science fiction`,
    historicalFiction: $localize`:@@kindleResultFictionHistorical:historical fiction`,
    horror: $localize`:@@kindleResultFictionHorror:darker suspense`,
    bookClubPicks: $localize`:@@kindleResultFictionBookClub:book-club favorites`,
    romantasy: $localize`:@@kindleResultFictionRomantasy:romantasy`,
    other: $localize`:@@kindleResultFictionOther:fiction`,
  };

  private readonly nonFictionTextMap: Record<NonFictionInterestId, string> = {
    selfImprovement: $localize`:@@kindleResultNonFictionSelf:self-improvement`,
    productivity: $localize`:@@kindleResultNonFictionProductivity:productivity`,
    businessCareers: $localize`:@@kindleResultNonFictionBusiness:business and career books`,
    relationshipsHealth: $localize`:@@kindleResultNonFictionHealth:relationships and health`,
    history: $localize`:@@kindleResultNonFictionHistory:history`,
    scienceTechnology: $localize`:@@kindleResultNonFictionScience:science and technology`,
    biographies: $localize`:@@kindleResultNonFictionBiographies:biographies`,
    philosophySpirituality: $localize`:@@kindleResultNonFictionPhilosophy:philosophy and spirituality`,
    other: $localize`:@@kindleResultNonFictionOther:non-fiction`,
  };

  generateMessages(params: KindleResultMessageParams): ResultMessages {
    const frictionIds = getAnswerIds<KindleFrictionId>(
      params.answers,
      'kindleFriction',
      this.frictionTextMap
    );
    const listeningTimeId = getFirstAnswerId<ListeningTimeId>(
      params.answers,
      'listeningTime',
      listeningPlans
    );
    const consumptionTimeIds = getAnswerIds<ConsumptionTimeId>(
      params.answers,
      'time',
      this.windowTextMap
    );
    const audioExperienceId =
      getFirstAnswerId<AudioExperienceId>(
        params.answers,
        'kindleAudioExperience',
        this.audioExperienceTextMap
      ) ||
      getFirstAnswerId<AudioExperienceId>(
        params.answers,
        'audioExperience',
        this.audioExperienceTextMap
      );
    const timeForReadingId = getFirstAnswerId<TimeForReadingId>(
      params.answers,
      'timeForReading',
      this.timeForReadingTextMap
    );
    const visionCareId = getFirstAnswerId<VisionCareId>(
      params.answers,
      'visionCare',
      this.visionCareTextMap
    );

    const listeningPlan =
      (listeningTimeId && listeningPlans[listeningTimeId]) || listeningPlans.minutes1530;
    const diagnosis = this.getDiagnosis(frictionIds, timeForReadingId, visionCareId);
    const frictionText = this.getFrictionText(frictionIds, visionCareId);
    const windowText = this.getWindowText(consumptionTimeIds);
    const shelfProfile = this.getShelfProfile(
      params.fictionInterestIds,
      params.nonFictionInterestIds
    );
    const sampleMatch = this.getSampleMatch(
      params.fictionInterestIds,
      params.nonFictionInterestIds
    );
    const audioFitText =
      (audioExperienceId && this.audioExperienceTextMap[audioExperienceId]) ||
      $localize`:@@kindleResultAudioFitDefault:The first win is making book audio feel natural enough to keep using.`;
    const comfortPriority = this.getComfortPriority(
      frictionIds,
      visionCareId,
      params.motivationIds
    );
    const metric = getMomentumScore({
      frictionCount: frictionIds.length,
      timeForReadingId,
      visionCareId,
      audioExperienceId,
      potentialLift: listeningPlan.potentialLift,
    });
    const timeframe = $localize`:@@kindleResultMetricTimeframe:3 months`;

    const messages = [
      {
        text: $localize`:@@kindleResultPersonalizedMsg1:Your Kindle library is not stuck because you lack discipline. Based on your answers, the pattern is clear: ${diagnosis}:DIAGNOSIS: Your biggest blockers are ${frictionText}:FRICTION:.`,
      },
      {
        text: $localize`:@@kindleResultPersonalizedMsg2:The best plan for you is ${listeningPlan.dailyLabel}:DAILY_LABEL:, mostly during ${windowText}:WINDOWS:. That is enough to ${listeningPlan.progressOutcome}:PROGRESS_OUTCOME: without needing a perfect quiet reading session.`,
      },
      {
        text: $localize`:@@kindleResultPersonalizedMsg3:For your ${shelfProfile}:SHELF_PROFILE:, Peech should optimize around ${comfortPriority}:COMFORT_PRIORITY:: natural long-form voices, easy resume, and pace control for dense or slow chapters. ${audioFitText}:AUDIO_FIT:`,
      },
      {
        text: $localize`:@@kindleResultPersonalizedMsg4:Your Reading Momentum Score is estimated at ${metric.currentValue}:CURRENT_VALUE:%. With this plan, it can realistically reach ${metric.potentialValue}:POTENTIAL_VALUE:% in ${timeframe}:TIMEFRAME: because the routine uses the time you already have.`,
      },
      {
        text: $localize`:@@kindleResultPersonalizedMsg5:Next, we will unlock the exact plan: Kindle-style listening, voices selected for long sessions, speed control, and samples matched to ${sampleMatch}:SAMPLE_MATCH:.`,
      },
    ];

    return {
      messages,
      metricProperties: {
        metric_name: 'Reading Momentum Score',
        metric_abbreviation: 'RMS',
        current_value: metric.currentValue,
        potential_value: metric.potentialValue,
        timeframe,
        friction_count: frictionIds.length,
        listening_time: listeningTimeId,
        audio_experience: audioExperienceId,
      },
    };
  }

  private readonly audioExperienceTextMap: Record<AudioExperienceId, string> = {
    yesRegularly: $localize`:@@kindleResultAudioRegular:You already know audio can fit your day, so the plan can focus on making Kindle-length books feel comfortable.`,
    yesOccasionally: $localize`:@@kindleResultAudioOccasional:You already use audio occasionally, so the plan should make it easier to return to books consistently.`,
    noButInterested: $localize`:@@kindleResultAudioInterested:You are open to audio, so the first win is making the first sessions feel easy and natural.`,
    noNotReally: $localize`:@@kindleResultAudioNew:The key is a low-friction start: familiar books, natural voices, and short sessions that do not feel like a new habit to manage.`,
  };

  private readonly timeForReadingTextMap: Record<TimeForReadingId, string> = {
    allTheTime: $localize`:@@kindleResultTimeAll:quiet page time is consistently hard to protect`,
    sometimes: $localize`:@@kindleResultTimeSometimes:reading time appears in uneven pockets`,
    notReally: $localize`:@@kindleResultTimeNotReally:you already read, and audio can extend that habit into moments when the page is not practical`,
  };

  private readonly visionCareTextMap: Record<VisionCareId, string> = {
    gettingWorse: $localize`:@@kindleResultVisionGettingWorse:eye comfort is a real constraint`,
    aBit: $localize`:@@kindleResultVisionABit:eye comfort matters`,
    allGood: $localize`:@@kindleResultVisionAllGood:eye comfort is stable`,
  };

  private getDiagnosis(
    frictionIds: KindleFrictionId[],
    timeForReadingId: TimeForReadingId | undefined,
    visionCareId: VisionCareId | undefined
  ): string {
    const primaryFriction = this.frictionPriority.find(id => frictionIds.includes(id));

    if (!primaryFriction && hasEyeComfortConcern(visionCareId)) {
      return $localize`:@@kindleResultDiagnosisEyeComfort:this is an eye-comfort problem, not a motivation problem`;
    }

    if (primaryFriction === 'noQuietTime' || primaryFriction === 'wantHandsFree') {
      return $localize`:@@kindleResultDiagnosisAccess:this is a time-access problem, not a motivation problem`;
    }

    if (primaryFriction === 'fallAsleep') {
      return $localize`:@@kindleResultDiagnosisEnergy:this is an energy problem, not a discipline problem`;
    }

    if (primaryFriction === 'longChapters') {
      return $localize`:@@kindleResultDiagnosisRestart:this is a restart-friction problem, especially with longer chapters`;
    }

    if (primaryFriction === 'voicesMatter') {
      return $localize`:@@kindleResultDiagnosisQuality:this is a quality problem because robotic audio breaks immersion`;
    }

    if (timeForReadingId) {
      return this.timeForReadingTextMap[timeForReadingId];
    }

    return $localize`:@@kindleResultDiagnosisDefault:your reading routine needs easier access to the books you already chose`;
  }

  private getFrictionText(
    frictionIds: KindleFrictionId[],
    visionCareId: VisionCareId | undefined
  ): string {
    const blockers = frictionIds.map(id => this.frictionTextMap[id]);

    if (hasEyeComfortConcern(visionCareId)) {
      blockers.push(this.visionCareTextMap[visionCareId]);
    }

    if (blockers.length === 0) {
      return $localize`:@@kindleResultFrictionDefault:finding enough quiet reading time`;
    }

    return formatList(blockers);
  }

  private getWindowText(consumptionTimeIds: ConsumptionTimeId[]): string {
    if (consumptionTimeIds.length === 0) {
      return $localize`:@@kindleResultWindowsDefault:walks, chores, commutes, and screen-free breaks`;
    }

    return formatList(consumptionTimeIds.map(id => this.windowTextMap[id]));
  }

  private getShelfProfile(
    fictionInterestIds: FictionInterestId[],
    nonFictionInterestIds: NonFictionInterestId[]
  ): string {
    const fictionText = this.getFirstMappedText(fictionInterestIds, this.fictionTextMap);
    const nonFictionText = this.getFirstMappedText(nonFictionInterestIds, this.nonFictionTextMap);

    if (fictionText && nonFictionText) {
      return $localize`:@@kindleResultShelfBalanced:balanced shelf of ${fictionText}:FICTION: plus ${nonFictionText}:NON_FICTION:`;
    }

    if (fictionText) {
      return $localize`:@@kindleResultShelfFiction:story-first shelf with ${fictionText}:FICTION:`;
    }

    if (nonFictionText) {
      return $localize`:@@kindleResultShelfNonFiction:ideas-and-growth shelf with ${nonFictionText}:NON_FICTION:`;
    }

    return $localize`:@@kindleResultShelfDefault:long-form reading list`;
  }

  private getSampleMatch(
    fictionInterestIds: FictionInterestId[],
    nonFictionInterestIds: NonFictionInterestId[]
  ): string {
    const selectedInterests = [
      ...fictionInterestIds.map(id => this.fictionTextMap[id]).filter(Boolean),
      ...nonFictionInterestIds.map(id => this.nonFictionTextMap[id]).filter(Boolean),
    ];

    if (selectedInterests.length === 0) {
      return $localize`:@@kindleResultSampleDefault:your reading tastes`;
    }

    return formatList(selectedInterests);
  }

  private getComfortPriority(
    frictionIds: KindleFrictionId[],
    visionCareId: VisionCareId | undefined,
    motivationIds: MotivationId[]
  ): string {
    if (hasEyeComfortConcern(visionCareId) || motivationIds.includes('reduceEyeStrain')) {
      return $localize`:@@kindleResultPriorityComfort:screen-free comfort`;
    }

    if (frictionIds.includes('voicesMatter')) {
      return $localize`:@@kindleResultPriorityVoice:narration quality`;
    }

    if (frictionIds.includes('longChapters')) {
      return $localize`:@@kindleResultPriorityRestart:low-friction chapter restart`;
    }

    return $localize`:@@kindleResultPriorityContinuity:listening continuity`;
  }

  private getFirstMappedText<T extends string>(
    ids: T[],
    map: Record<T, string>
  ): string | undefined {
    const id = ids.find(value => isAllowedValue(value, map));

    return id ? map[id] : undefined;
  }
}
